/**
 * Guest identity management
 *
 * Manages persistent guest identity via localStorage.
 * The guestId is a UUID that survives browser tab closes
 * and is used as the credential for POST /auth/guest.
 */

import {
  GUEST_EMAIL_SUFFIX,
  GUEST_NICKNAME_STORAGE_KEY,
  GUEST_SHARE_STORAGE_PREFIX,
  GUEST_STORAGE_KEY,
} from "./constants";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface GuestShareCredential {
  gameType: string;
  roomNumber: string;
  username: string;
  secret: string;
  guestId: string;
  savedAt: string;
  lastUsedAt: string;
}

export interface GuestIdSeedInput {
  gameType: string;
  roomNumber: string;
  username: string;
  secret: string;
}

export interface GuestNicknamePreference {
  guestId: string;
  nickname: string;
  savedAt: string;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function createDeterministicUuidFromHex(hex: string): string {
  const chars = hex.slice(0, 32).padEnd(32, "0").split("");

  chars[12] = "4";
  const variantNibble = Number.parseInt(chars[16] ?? "0", 16);
  chars[16] = ((variantNibble & 0x3) | 0x8).toString(16);

  const uuid = chars.join("");
  return `${uuid.slice(0, 8)}-${uuid.slice(8, 12)}-${uuid.slice(12, 16)}-${uuid.slice(16, 20)}-${uuid.slice(20, 32)}`;
}

function buildShareCredentialStorageKey(
  gameType: string,
  roomNumber: string,
  storagePrefix = GUEST_SHARE_STORAGE_PREFIX,
): string {
  return `${storagePrefix}:${gameType}:${roomNumber}`;
}

function isValidGuestShareCredential(
  value: unknown,
): value is GuestShareCredential {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as GuestShareCredential).gameType === "string" &&
    typeof (value as GuestShareCredential).roomNumber === "string" &&
    typeof (value as GuestShareCredential).username === "string" &&
    typeof (value as GuestShareCredential).secret === "string" &&
    typeof (value as GuestShareCredential).guestId === "string" &&
    typeof (value as GuestShareCredential).savedAt === "string" &&
    typeof (value as GuestShareCredential).lastUsedAt === "string"
  );
}

function isValidGuestNicknamePreference(
  value: unknown,
): value is GuestNicknamePreference {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as GuestNicknamePreference).guestId === "string" &&
    typeof (value as GuestNicknamePreference).nickname === "string" &&
    typeof (value as GuestNicknamePreference).savedAt === "string"
  );
}

/** Read guestId from localStorage */
export function getGuestId(storageKey = GUEST_STORAGE_KEY): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/** Read or generate + save a guestId. Validates existing value is a UUID. */
export function getOrCreateGuestId(storageKey = GUEST_STORAGE_KEY): string {
  const existing = getGuestId(storageKey);
  if (existing && UUID_REGEX.test(existing)) {
    return existing;
  }
  const id = crypto.randomUUID();
  saveGuestId(id, storageKey);
  return id;
}

/**
 * Derive a deterministic UUID guest credential from share-link fields.
 * This allows reconnect/rejoin with the same guest identity.
 */
export async function deriveDeterministicGuestId(
  input: GuestIdSeedInput,
): Promise<string> {
  const normalizedSeed = [
    input.gameType.trim().toLowerCase(),
    input.roomNumber.trim(),
    input.username.trim().toLowerCase(),
    input.secret.trim(),
  ].join(":");
  const encoded = new TextEncoder().encode(normalizedSeed);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return createDeterministicUuidFromHex(toHex(new Uint8Array(digest)));
}

/** Write guestId to localStorage */
export function saveGuestId(id: string, storageKey = GUEST_STORAGE_KEY): void {
  try {
    localStorage.setItem(storageKey, id);
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Read the nickname used by the currently persisted guest identity.
 */
export function getSavedGuestNickname(
  guestStorageKey = GUEST_STORAGE_KEY,
  nicknameStorageKey = GUEST_NICKNAME_STORAGE_KEY,
): string | null {
  const guestId = getGuestId(guestStorageKey);
  if (!guestId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(nicknameStorageKey);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!isValidGuestNicknamePreference(parsed)) {
      localStorage.removeItem(nicknameStorageKey);
      return null;
    }

    return parsed.guestId === guestId ? parsed.nickname : null;
  } catch {
    return null;
  }
}

/**
 * Save the nickname chosen for a guest identity.
 */
export function saveGuestNickname(
  guestId: string,
  nickname: string,
  nicknameStorageKey = GUEST_NICKNAME_STORAGE_KEY,
): void {
  const trimmedNickname = nickname.trim();
  if (!guestId || !trimmedNickname) {
    return;
  }

  const preference: GuestNicknamePreference = {
    guestId,
    nickname: trimmedNickname,
    savedAt: new Date().toISOString(),
  };

  try {
    localStorage.setItem(nicknameStorageKey, JSON.stringify(preference));
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Read saved share-link guest credential from localStorage.
 */
export function getGuestShareCredential(
  gameType: string,
  roomNumber: string,
  storagePrefix = GUEST_SHARE_STORAGE_PREFIX,
): GuestShareCredential | null {
  const key = buildShareCredentialStorageKey(
    gameType,
    roomNumber,
    storagePrefix,
  );

  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!isValidGuestShareCredential(parsed)) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Save share-link guest credential to localStorage.
 */
export function saveGuestShareCredential(
  credential: Omit<GuestShareCredential, "savedAt" | "lastUsedAt">,
  storagePrefix = GUEST_SHARE_STORAGE_PREFIX,
): void {
  const key = buildShareCredentialStorageKey(
    credential.gameType,
    credential.roomNumber,
    storagePrefix,
  );
  const now = new Date().toISOString();
  const value: GuestShareCredential = {
    ...credential,
    savedAt: now,
    lastUsedAt: now,
  };

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Remove share-link guest credential from localStorage.
 */
export function clearGuestShareCredential(
  gameType: string,
  roomNumber: string,
  storagePrefix = GUEST_SHARE_STORAGE_PREFIX,
): void {
  const key = buildShareCredentialStorageKey(
    gameType,
    roomNumber,
    storagePrefix,
  );
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Update lastUsedAt timestamp for saved share-link guest credential.
 */
export function touchGuestShareCredential(
  gameType: string,
  roomNumber: string,
  storagePrefix = GUEST_SHARE_STORAGE_PREFIX,
): void {
  const existing = getGuestShareCredential(gameType, roomNumber, storagePrefix);
  if (!existing) {
    return;
  }

  const key = buildShareCredentialStorageKey(
    gameType,
    roomNumber,
    storagePrefix,
  );
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        ...existing,
        lastUsedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // localStorage may be unavailable
  }
}

/** Remove guestId from localStorage */
export function clearGuestId(storageKey = GUEST_STORAGE_KEY): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage errors
  }
}

/** Check if an email belongs to a guest user */
export function isGuestEmail(email: string): boolean {
  return email.endsWith(GUEST_EMAIL_SUFFIX);
}

/** Format a guest email as a short display label (e.g. "Guest a1b2") */
export function formatGuestLabel(email: string): string {
  if (!isGuestEmail(email)) {
    return email;
  }
  const prefix = "guest-";
  const uuidPart = email.slice(
    prefix.length,
    email.length - GUEST_EMAIL_SUFFIX.length,
  );
  return `Guest ${uuidPart.slice(-4)}`;
}
